// The Blueprint (desktop): hairline architecture bleeding off the top-right of the hero.
// Far plane: a skyline of wireframe blocks stepping down toward the headline. Near plane: a constellation along the skyline and three solid blue squares.
// Everything stays right of the copy. Each plane rides its own scroll rate; the whole piece
// tilts a few degrees toward the pointer. Theme-aware through --ink / --paper.
// Phones get their own composition: see blueprint-mobile.tsx.
import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { BlueprintFar } from './blueprint-far';

export function Blueprint({ className = '' }: { className?: string }) {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const farY = useTransform(scrollY, [0, 900], [0, reduce ? 0 : -50]);
  const nearY = useTransform(scrollY, [0, 900], [0, reduce ? 0 : -200]);
  const fade = useTransform(scrollY, [0, 700], [1, 0.2]);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const raf = useRef(0);
  useEffect(() => {
    if (reduce) return;
    const onMove = (e: MouseEvent) => {
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(() => {
        const nx = e.clientX / window.innerWidth - 0.5;
        const ny = e.clientY / window.innerHeight - 0.5;
        setTilt({ x: ny * -6, y: nx * 10 });
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      cancelAnimationFrame(raf.current);
    };
  }, [reduce]);

  const plane = 'absolute inset-0 h-full w-full';
  return (
    <div
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      aria-hidden="true"
      style={{ perspective: '1400px' }}
    >
      <motion.div
        className="absolute inset-0"
        style={{
          opacity: fade,
          transformStyle: 'preserve-3d',
          rotateX: tilt.x,
          rotateY: tilt.y,
          transformOrigin: '85% 20%',
        }}
        transition={{ type: 'spring', stiffness: 60, damping: 20 }}
      >
        <BlueprintFar y={farY} className={plane} />
        <motion.svg
          className={plane}
          viewBox="0 0 1600 900"
          preserveAspectRatio="xMaxYMin slice"
          style={{ y: nearY }}
        >
          <path
            d="M 1100 290 L 1180 220"
            stroke="var(--ink, #0a0a0b)"
            strokeWidth="1"
            opacity=".35"
          />
          <path
            d="M 1180 220 L 1260 140"
            stroke="var(--ink, #0a0a0b)"
            strokeWidth="1"
            opacity=".35"
          />
          <path
            d="M 1260 140 L 1360 40"
            stroke="var(--ink, #0a0a0b)"
            strokeWidth="1"
            opacity=".35"
          />
          <path
            d="M 1360 40 L 1440 -20"
            stroke="var(--ink, #0a0a0b)"
            strokeWidth="1"
            opacity=".35"
          />
          <path
            d="M 1440 -20 L 1540 -70"
            stroke="var(--ink, #0a0a0b)"
            strokeWidth="1"
            opacity=".35"
          />
          <circle
            cx="1100"
            cy="290"
            r="4"
            fill="var(--paper, #fafafa)"
            stroke="var(--ink, #0a0a0b)"
            strokeWidth="1.5"
            opacity=".9"
          />
          <circle
            cx="1180"
            cy="220"
            r="4"
            fill="var(--paper, #fafafa)"
            stroke="var(--ink, #0a0a0b)"
            strokeWidth="1.5"
            opacity=".9"
          />
          <circle
            cx="1260"
            cy="140"
            r="4"
            fill="var(--paper, #fafafa)"
            stroke="var(--ink, #0a0a0b)"
            strokeWidth="1.5"
            opacity=".9"
          />
          <circle
            cx="1360"
            cy="40"
            r="4"
            fill="var(--paper, #fafafa)"
            stroke="var(--ink, #0a0a0b)"
            strokeWidth="1.5"
            opacity=".9"
          />
          <circle
            cx="1440"
            cy="-20"
            r="4"
            fill="var(--paper, #fafafa)"
            stroke="var(--ink, #0a0a0b)"
            strokeWidth="1.5"
            opacity=".9"
          />
          <circle
            cx="1540"
            cy="-70"
            r="4"
            fill="var(--paper, #fafafa)"
            stroke="var(--ink, #0a0a0b)"
            strokeWidth="1.5"
            opacity=".9"
          />
          <rect x="1060" y="180" width="34" height="34" fill="#2563eb" />
          <rect x="1108" y="180" width="34" height="34" fill="#2563eb" />
          <rect x="1156" y="180" width="34" height="34" fill="#2563eb" />
        </motion.svg>
      </motion.div>
    </div>
  );
}

export default Blueprint;
